import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsResponse } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Button, Card, TextField } from '../../components/ui';

const Result = z.object({ set: z.boolean() });

/** One write-only secret: shows whether a value is stored, never the value. Saving an empty box clears it. */
function SecretField({ label, url, moduleKey, path, canEdit }: { label: string; url: string; moduleKey: string; path: [string, string]; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState('');
  const settings = useQuery({
    queryKey: ['platform', 'settings', moduleKey],
    queryFn: () => api.get(`/platform/settings/${moduleKey}`, { schema: SettingsResponse }),
  });
  const group = settings.data?.values[path[0]] as Record<string, unknown> | undefined;
  const isSet = group?.[path[1]] === 'set';
  const save = useMutation({
    mutationFn: () => api.put(url, { body: { value }, schema: Result }),
    onSuccess: async () => {
      setValue('');
      await qc.invalidateQueries({ queryKey: ['platform', 'settings', moduleKey] });
      toast(t('platformSecrets.saved'));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={submit} className="flex items-end gap-3" aria-label={label}>
      <TextField
        label={label}
        type="password"
        autoComplete="new-password"
        value={value}
        disabled={!canEdit}
        placeholder={isSet ? t('platformSecrets.isSet') : t('platformSecrets.notSet')}
        hint={t('platformSecrets.hint')}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1"
      />
      <Button type="submit" disabled={!canEdit || save.isPending || (value === '' && !isSet)}>
        {value === '' ? t('platformSecrets.clear') : t('platformSecrets.set')}
      </Button>
      <ErrorLine error={save.error} />
    </form>
  );
}

/** Write-only Microsoft Entra client secret. */
export function PlatformEntraSecret({ canEdit }: { canEdit: boolean }) {
  return <SecretField label={t('platformSecrets.entra')} url="/platform/secrets/entra-client-secret" moduleKey="core" path={['entra', 'clientSecretEnc']} canEdit={canEdit} />;
}

/** Write-only Expo push access token. */
export function PlatformExpoSecret({ canEdit }: { canEdit: boolean }) {
  return <SecretField label={t('platformSecrets.expo')} url="/platform/secrets/expo-access-token" moduleKey="notifications" path={['push', 'expoAccessTokenEnc']} canEdit={canEdit} />;
}

/** Both write-only secrets in one card. The platform settings page now shows each on its own tab. */
export function PlatformSecretsSection({ canEdit }: { canEdit: boolean }) {
  return (
    <div className="mt-8">
      <Card title={t('platformSecrets.title')} description={t('platformSecrets.description')}>
        <div className="space-y-4">
          <PlatformEntraSecret canEdit={canEdit} />
          <PlatformExpoSecret canEdit={canEdit} />
        </div>
      </Card>
    </div>
  );
}
